import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import ContentHeaderStyled from '../../../components/ContentHeader';
import PaginationStyled from '../../../components/Pagination';
import { LIBRARY_LINKS } from '../../../constants';
import { Button, IconDownload as Download } from '../../../core';
import { useDownloadFile } from '../../../hooks/useDownloadFile';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const FigmaLibraryStructure = ({ className = '' }: Props) => {
  const downloadFile = useDownloadFile();

  return (
    <div className={className}>
      <div className="content">
        <ContentHeaderStyled
          title="Figma Library"
          breadcrumbs={[
            { label: 'Dev Resources', url: '/dev-resources' },
            { label: 'Figma Library', url: '/' },
          ]}
        />
        <div className="page-container">
          <div className="page-content">
            <div className="page-content-with-navigation-wrapper">
              <div className="page-content-with-navigation">
                <div className="title-with-download-button">
                  <h2 className="h1" id="data-components">
                    Data Visualisation Components
                  </h2>
                  <Button
                    size="M"
                    iconAfter={<Download />}
                    onPointerDown={() =>
                      downloadFile('Figma Library.fig', LIBRARY_LINKS.figma_lib_path)
                    }
                  >
                    Download Figma Library
                  </Button>
                </div>
                <div className="page-content-item mb-40">
                  <p className="font-sm">
                    Get to know the basics of accessible design before diving into Material
                    accessibility specs, patterns, and platform-specific guidance.
                  </p>
                </div>
                <img
                  src="/images/dev-resources-figma-library/figma-library-1@3x.jpg"
                  alt="Figma Library"
                  className="mb-40 img-responsive"
                />
                <div className="page-content-item">
                  <h3 className="h3" id="kit-overview">
                    Kit overview
                  </h3>
                  <div className="pseudo-list">
                    <span className="pseudo-list-nr">1</span>
                    <p>
                      <strong>The Figma frame has dimensions 1280px by 720px,</strong> the correct
                      size for PowerBI dashboard.
                    </p>
                  </div>
                </div>
                <img
                  src="/images/dev-resources-figma-library/figma-library-2@3x.jpg"
                  alt="Figma Library"
                  className="mb-40 img-responsive"
                />
                <div className="page-content-item">
                  <div
                    className="visually-hidden"
                    aria-hidden="true"
                    id="charts-and-components-settings"
                  >
                    Charts settings
                  </div>
                  <h3 className="h3">Charts & components settings</h3>
                  <div className="pseudo-list">
                    <span className="pseudo-list-nr">2</span>
                    <p>
                      <strong>Add dynamic visualizations, interactive elements,</strong> and
                      personalized insights to create a truly immersive data experience:
                    </p>
                    <ol className="disc">
                      <li>Insert charts, graphs, and tables to visualize data.</li>
                      <li>
                        Incorporate slicers, filters, and drill-down features for interactivity.
                      </li>
                      <li>
                        Include text boxes or annotations to provide insights or explanations.
                      </li>
                    </ol>
                  </div>
                </div>
                <img
                  src="/images/dev-resources-figma-library/figma-library-3@3x.jpg"
                  alt="Figma Library"
                  className="mb-40 img-responsive"
                />
                <div className="page-content-item">
                  <div className="pseudo-list">
                    <span className="pseudo-list-nr">3</span>
                    <p>
                      <strong>Add dynamic visualizations, interactive elements,</strong> and
                      personalized insights to create a truly immersive data experience:
                    </p>
                    <ol className="disc">
                      <li>Insert charts, graphs, and tables to visualize data.</li>
                      <li>
                        Incorporate slicers, filters, and drill-down features for interactivity.
                      </li>
                      <li>
                        Include text boxes or annotations to provide insights or explanations.
                      </li>
                    </ol>
                  </div>
                </div>
                <img
                  src="/images/dev-resources-figma-library/figma-library-4@3x.jpg"
                  alt="Figma Library"
                  className="mb-40 img-responsive"
                />
                <div className="page-content-item">
                  <div className="pseudo-list">
                    <span className="pseudo-list-nr">4</span>
                    <p>
                      <strong>Add dynamic visualizations, interactive elements,</strong> and
                      personalized insights to create a truly immersive data experience:
                    </p>
                    <ol className="disc">
                      <li>Insert charts, graphs, and tables to visualize data.</li>
                      <li>
                        Incorporate slicers, filters, and drill-down features for interactivity.
                      </li>
                      <li>
                        Include text boxes or annotations to provide insights or explanations.
                      </li>
                    </ol>
                  </div>
                </div>
                <img
                  src="/images/dev-resources-figma-library/figma-library-5@3x.jpg"
                  alt="Figma Library"
                  className="mb-40 img-responsive"
                />
                <div className="page-content-item">
                  <h3 className="h3" id="typography-styles">
                    Typography Styles
                  </h3>
                  <div className="pseudo-list">
                    <span className="pseudo-list-nr">5</span>
                    <p>
                      <strong>Add dynamic visualizations, interactive elements,</strong> and
                      personalized insights to create a truly immersive data experience:
                    </p>
                    <ol className="disc">
                      <li>Insert charts, graphs, and tables to visualize data.</li>
                      <li>
                        Incorporate slicers, filters, and drill-down features for interactivity.
                      </li>
                      <li>
                        Include text boxes or annotations to provide insights or explanations.
                      </li>
                    </ol>
                  </div>
                </div>
                <img
                  src="/images/dev-resources-figma-library/figma-library-6@3x.jpg"
                  alt="Figma Library"
                  className="mb-40 img-responsive"
                />
                <div className="page-content-item">
                  <h3 className="h3" id="connecting-figma-kit">
                    Connecting Figma Kit
                  </h3>
                  <div className="pseudo-list">
                    <span className="pseudo-list-nr">6</span>
                    <p>
                      <strong>Add dynamic visualizations, interactive elements,</strong> and
                      personalized insights to create a truly immersive data experience:
                    </p>
                    <ol className="disc">
                      <li>Insert charts, graphs, and tables to visualize data.</li>
                      <li>
                        Incorporate slicers, filters, and drill-down features for interactivity.
                      </li>
                      <li>
                        Include text boxes or annotations to provide insights or explanations.
                      </li>
                    </ol>
                  </div>
                </div>
                <img
                  src="/images/dev-resources-figma-library/figma-library-7@3x.jpg"
                  alt="Figma Library"
                  className="img-responsive"
                />
              </div>
              <div>
                <div className="page-navigation">
                  <h2>Contents</h2>
                  <Scrollspy
                    ids={[
                      'data-components',
                      'kit-overview',
                      'charts-and-components-settings',
                      'typography-styles',
                      'connecting-figma-kit',
                    ]}
                    offset={0}
                    activeItemClassName="active"
                    itemContainerClassName="multilevel-link-list"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <PaginationStyled previousLink="/dev-resources/esrikit" previous="ESRI Library" />
      </div>
    </div>
  );
};

const FigmaLibrary = styled(FigmaLibraryStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 3, 4, 5])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }
`;

export default FigmaLibrary;
